import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { BillToPartnerPicker, type BillToPartnerOption } from "./BillToPartnerPicker";

interface Props {
  contractId: number;
  updatedAt: string;
  currentValue: BillToPartnerOption | null;
}

interface EditSnapshot {
  contractId: number;
  updatedAt: string;
  currentValue: BillToPartnerOption | null;
}

interface EditDraft {
  snapshot: EditSnapshot;
  selected: BillToPartnerOption | null;
}

function copyValue(value: BillToPartnerOption | null): BillToPartnerOption | null {
  return value ? { id: value.id, partnerCode: value.partnerCode, legalName: value.legalName } : null;
}

function sameValue(a: BillToPartnerOption | null, b: BillToPartnerOption | null): boolean {
  return a?.id === b?.id && a?.partnerCode === b?.partnerCode && a?.legalName === b?.legalName;
}

function sameSnapshot(a: EditSnapshot, b: EditSnapshot): boolean {
  return a.contractId === b.contractId && a.updatedAt === b.updatedAt && sameValue(a.currentValue, b.currentValue);
}

function saveErrorMessage(error: unknown): string {
  const message = error && typeof error === "object" && "message" in error
    ? String((error as { message?: unknown }).message ?? "")
    : "";
  if (message.includes("CONTRACT_STALE_UPDATE")) {
    return "El contrato cambió mientras editabas. Se ha recargado; vuelve a revisar antes de guardar.";
  }
  if (message.includes("CONTRACT_ARCHIVED")) {
    return "El contrato está archivado y no admite cambios de partes.";
  }
  return "No se pudo actualizar Facturar a. Comprueba el contrato y vuelve a intentarlo.";
}

export function ContractBillToEditor({ contractId, updatedAt, currentValue }: Props) {
  const utils = trpc.useUtils();
  const replaceRole = trpc.customers.contracts.replacePrimaryPartnerRole.useMutation();
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [reviewNeeded, setReviewNeeded] = useState(false);
  const currentSnapshot: EditSnapshot = {
    contractId,
    updatedAt,
    currentValue: copyValue(currentValue),
  };
  const currentSnapshotRef = useRef(currentSnapshot);
  currentSnapshotRef.current = currentSnapshot;
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const snapshotCurrent = draft != null && sameSnapshot(draft.snapshot, currentSnapshot);

  useEffect(() => {
    const activeDraft = draftRef.current;
    if (activeDraft && !sameSnapshot(activeDraft.snapshot, currentSnapshotRef.current)) {
      draftRef.current = null;
      setDraft(null);
      setReviewNeeded(true);
    }
  }, [contractId, updatedAt, currentValue?.id, currentValue?.partnerCode, currentValue?.legalName]);

  const hasChange = snapshotCurrent && draft.selected != null && draft.selected.id !== draft.snapshot.currentValue?.id;

  function startEditing() {
    const nextDraft: EditDraft = {
      snapshot: {
        contractId,
        updatedAt,
        currentValue: copyValue(currentValue),
      },
      selected: copyValue(currentValue),
    };
    draftRef.current = nextDraft;
    setDraft(nextDraft);
    setReviewNeeded(false);
  }

  function cancelEditing() {
    draftRef.current = null;
    setDraft(null);
  }

  function selectPartner(value: BillToPartnerOption | null) {
    const activeDraft = draftRef.current;
    if (!activeDraft || !sameSnapshot(activeDraft.snapshot, currentSnapshotRef.current)) return;
    const nextDraft = { ...activeDraft, selected: value };
    draftRef.current = nextDraft;
    setDraft(nextDraft);
  }

  function closeDraftAfterSave(savedSnapshot: EditSnapshot) {
    const activeDraft = draftRef.current;
    if (activeDraft && sameSnapshot(activeDraft.snapshot, savedSnapshot)) {
      draftRef.current = null;
      setDraft(null);
    }
  }

  async function save() {
    const activeDraft = draftRef.current;
    if (!activeDraft) return;
    if (!sameSnapshot(activeDraft.snapshot, currentSnapshotRef.current)) {
      draftRef.current = null;
      setDraft(null);
      setReviewNeeded(true);
      return;
    }
    if (!hasChange || !activeDraft.selected) return;
    const request = {
      contractId: activeDraft.snapshot.contractId,
      role: "bill_to" as const,
      newBusinessPartnerId: activeDraft.selected.id,
      expectedUpdatedAt: activeDraft.snapshot.updatedAt,
    };
    try {
      await replaceRole.mutateAsync(request);
      try {
        await utils.customers.contracts.get.invalidate({ contractId: request.contractId });
      } catch {
        // The next detail read remains the source of truth.
      }
      toast.success("Facturar a actualizado. La Parte contratante no ha cambiado.");
      closeDraftAfterSave(activeDraft.snapshot);
    } catch (error) {
      try {
        await utils.customers.contracts.get.invalidate({ contractId: request.contractId });
      } catch {
        // Keep the form closed after a stale write; the page can be reloaded.
      }
      toast.error(saveErrorMessage(error));
      closeDraftAfterSave(activeDraft.snapshot);
    }
  }

  if (!snapshotCurrent) {
    return (
      <div className="space-y-2">
        {(reviewNeeded || draft != null) && (
          <p role="alert" className="rounded-md border border-amber-500/50 p-3 text-sm">
            El contrato o «Facturar a» cambió desde que abriste la edición. Se descartó la selección; revisa el valor actual antes de volver a guardar.
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          Cambiar Facturar a no modifica la Parte contratante ni los datos económicos del contrato.
        </p>
        <Button type="button" variant="outline" className="h-auto min-h-11 whitespace-normal" onClick={startEditing}>
          {currentValue ? "Cambiar Facturar a" : "Configurar Facturar a"}
        </Button>
      </div>
    );
  }

  return (
    <div className="min-w-0 max-w-full space-y-3 rounded-md border p-3">
      <BillToPartnerPicker value={draft.selected} onChange={selectPartner} disabled={replaceRole.isPending} />
      <div className="flex min-w-0 flex-wrap gap-2">
        <Button type="button" variant="outline" className="h-auto min-h-11 whitespace-normal" disabled={replaceRole.isPending} onClick={cancelEditing}>
          Cancelar
        </Button>
        <Button type="button" className="h-auto min-h-11 whitespace-normal" disabled={!hasChange || replaceRole.isPending} onClick={() => void save()}>
          {replaceRole.isPending ? "Guardando…" : "Guardar Facturar a"}
        </Button>
      </div>
    </div>
  );
}
